import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "./utils";

/**
 * Иерархия из §10 «Контролы»:
 *
 *  - primary  — основной CTA, один на экран
 *  - ai       — запуск/подтверждение AI-действия
 *  - secondary — вторичные действия
 *  - ghost    — для toolbar и плотных таблиц
 *  - danger   — отмена сделки, удаление
 *
 * НЕ путать `primary` и `ai`. Решения человека (например подтверждение
 * оплаты) — primary, не ai.
 */
const buttonVariants = cva(
  [
    "inline-flex items-center justify-center gap-2",
    "font-semibold whitespace-nowrap select-none",
    "rounded-md",
    // Sprint AQ — semantic motion: "hover" intent maps to fast +
    // out-soft via tokens.css. Tweak the brand's feel in one place.
    "transition-colors duration-hover ease-hover",
    "focus-visible:outline-none focus-visible:shadow-focus",
    "disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none",
  ],
  {
    variants: {
      variant: {
        primary:   "bg-primary-700 text-white hover:bg-primary-800 active:bg-primary-900",
        ai:        "bg-ai-500 text-white hover:bg-ai-600 active:bg-ai-700",
        secondary: "bg-surface text-base-900 border border-base-300 hover:bg-base-50 active:bg-base-100",
        ghost:     "bg-transparent text-base-700 hover:bg-base-100 active:bg-base-200",
        danger:    "bg-danger-500 text-white hover:bg-danger-600 active:bg-danger-700",
      },
      size: {
        xs: "h-6 px-2 text-xs",
        sm: "h-8 px-3 text-sm",
        md: "h-10 px-4 text-md",
        lg: "h-12 px-5 text-lg",
      },
      iconOnly: {
        true: "px-0 aspect-square",
      },
    },
    defaultVariants: {
      variant: "secondary",
      size: "md",
      iconOnly: false,
    },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  loading?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, iconOnly, loading, leftIcon, rightIcon, children, disabled, ...props }, ref) => (
    <button
      ref={ref}
      type={props.type ?? "button"}
      disabled={disabled ?? loading}
      data-loading={loading || undefined}
      className={cn(buttonVariants({ variant, size, iconOnly, className }))}
      {...props}
    >
      {loading ? <Spinner /> : leftIcon}
      {children}
      {!loading && rightIcon}
    </button>
  ),
);

Button.displayName = "Button";

function Spinner(): ReactNode {
  return (
    <svg
      className="size-4 animate-spin"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
      <path
        d="M22 12a10 10 0 0 1-10 10"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
      />
    </svg>
  );
}
